/**
 * All contacts list.
 *
 * A navigation list, not a data table — one row per contact, click to open the
 * edit panel. Plain rows rather than AG Grid on purpose: there's nothing to sort
 * or column-align here, it's a master list you scan and click, so the grid engine
 * would be weight without payoff. (The repo's "tables are AG Grid" rule is for
 * multi-column record grids; this is the master-rail exception it carves out.)
 *
 * Each row: initials avatar · name over a meta line (company · email · phone) ·
 * role tag · chevron. The tag is the role as the model stores it, sentence case
 * and full width — a role is a NAME, and shouting it in 9px uppercase made two
 * long ones ("Buyer Real Estate Agent", "Appraisal Point of Contact") unreadable.
 * A role that is one of the loan's KEY roles gets the brand tint so the same
 * word means the same thing here as it does in the section above; every other
 * role stays neutral.
 *
 * Search filters the list in place.
 */
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useMemo } from "react";

import { formatPhone } from "../../../../../_src_shared/utils/functions/formatPhone";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { TAB_TOKENS as T } from "../../../shared/tabLayout/tabTokens";

const fullNameOf = (c) =>
  `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
  c?.emailAddress ||
  "Unnamed contact";

const initialsOf = (c) => {
  const first = (c?.firstName || "").charAt(0);
  const last = (c?.lastName || "").charAt(0);
  return (
    `${first}${last}`.toUpperCase() ||
    (c?.emailAddress || "?").charAt(0).toUpperCase()
  );
};

/** company · email · phone, joined by middot, empties dropped. */
const metaOf = (c) =>
  [c?.company, c?.emailAddress, c?.phone ? formatPhone(c.phone) : null]
    .filter(Boolean)
    .join("  ·  ");

const RoleTag = ({ role, isKeyRole }) => (
  <Box
    sx={{
      flexShrink: 0,
      fontSize: "11.5px",
      fontWeight: 500,
      lineHeight: 1.4,
      color: isKeyRole ? T.color.brand : T.color.ink2,
      background: isKeyRole ? T.color.brandSoft : "#eef2f6",
      border: `1px solid ${isKeyRole ? "transparent" : T.color.border}`,
      borderRadius: "6px",
      padding: "2px 9px",
      whiteSpace: "nowrap",
      [T.bp.twoUp]: { display: "none" },
    }}
  >
    {role}
  </Box>
);

const ContactRow = memo(({ contact, selected, onClick, isLast, isKeyRole }) => {
  const name = fullNameOf(contact);
  const meta = metaOf(contact);

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={`Edit ${name}`}
      sx={{
        font: "inherit",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        width: "100%",
        padding: "12px 16px",
        cursor: "pointer",
        border: "none",
        borderBottom: isLast ? "none" : `1px solid ${T.color.border}`,
        background: selected ? T.color.brandSoft : "transparent",
        transition: "background 120ms ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        "&:hover": {
          background: selected ? T.color.brandSoft : T.color.surfaceSunk,
          "& .row-chevron": { color: T.color.ink3 },
        },
        "&:focus-visible": {
          outline: `2px solid ${T.color.brand}`,
          outlineOffset: "-2px",
        },
      }}
    >
      {/* Avatar */}
      <Flx
        center
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: "999px",
          background: T.color.brandSoft,
          color: T.color.brand,
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {initialsOf(contact)}
      </Flx>

      {/* Name + meta */}
      <Flx column sx={{ gap: "2px", minWidth: 0, flex: 1 }}>
        <Txt
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: T.color.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Txt>
        <Txt
          sx={{
            fontSize: "12px",
            color: meta ? T.color.ink3 : T.color.muted,
            fontVariantNumeric: "tabular-nums",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {meta || "—"}
        </Txt>
      </Flx>

      {contact?.loanRole && (
        <RoleTag role={contact.loanRole} isKeyRole={isKeyRole} />
      )}

      <ChevronRightRounded
        className="row-chevron"
        sx={{ flexShrink: 0, fontSize: 18, color: T.color.muted }}
      />
    </Box>
  );
});

const matches = (contact, q) =>
  [
    fullNameOf(contact),
    contact?.company,
    contact?.emailAddress,
    contact?.phone,
    contact?.loanRole,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(q);

const AllContactsList = ({
  contacts,
  editingId,
  quickFilter,
  keyRoles,
  onSelect,
}) => {
  const q = (quickFilter || "").trim().toLowerCase();
  const rows = useMemo(
    () => (q ? contacts.filter((c) => matches(c, q)) : contacts),
    [contacts, q],
  );
  const keyRoleSet = useMemo(() => new Set(keyRoles || []), [keyRoles]);

  if (rows.length === 0) {
    return (
      <Flx center sx={{ padding: "28px 16px" }}>
        <Txt sx={{ ...T.type.pageSubtitle }}>
          No contacts match “{quickFilter}”.
        </Txt>
      </Flx>
    );
  }

  return (
    <Box>
      {rows.map((c, i) => (
        <ContactRow
          key={c._id}
          contact={c}
          selected={c._id === editingId}
          isLast={i === rows.length - 1}
          isKeyRole={keyRoleSet.has(c?.loanRole)}
          onClick={() => onSelect?.(c)}
        />
      ))}
    </Box>
  );
};

ContactRow.displayName = "ContactRow";

AllContactsList.propTypes = {
  contacts: PropTypes.array.isRequired,
  /** Contact currently open in the edit sidebar — highlighted. */
  editingId: PropTypes.string,
  quickFilter: PropTypes.string,
  /** Roles the Key roles section tracks — those tags get the brand tint. */
  keyRoles: PropTypes.array,
  onSelect: PropTypes.func,
};

export default memo(AllContactsList);
